import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Sparkles, FileBarChart2, Loader2, Users, Briefcase, Building2, Hash, MapPin,
  RefreshCw, CalendarClock, HardHat, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';

export const NextDayReportPage = () => {
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const [date, setDate] = useState(tomorrow);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await reportsAPI.nextDay(d);
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const quick = [
    { label: 'Tomorrow', value: tomorrow },
    { label: 'Today', value: format(new Date(), 'yyyy-MM-dd') },
    { label: 'In 3 days', value: format(addDays(new Date(), 3), 'yyyy-MM-dd') },
  ];

  const humanDate = (() => {
    try { return format(parseISO(date), 'EEEE, d MMMM yyyy'); } catch { return date; }
  })();

  return (
    <div className="space-y-5 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            Next-Day Report
            <span className="text-xs font-semibold px-2 py-1 rounded-full brand-gradient text-white flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI
            </span>
          </h1>
          <p className="text-muted-foreground mt-1">Tomorrow's jobs and the exact trades each one needs — auto-generated daily</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-secondary/50 rounded-xl p-1">
            {quick.map((q) => (
              <button
                key={q.label}
                onClick={() => setDate(q.value)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${date === q.value ? 'brand-gradient text-white' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 bg-input border-border" data-testid="report-date" />
          <Button variant="outline" size="icon" onClick={() => load(date)} data-testid="refresh-report"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-72 gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm">Generating AI briefing…</p>
        </div>
      ) : !report || report.total_jobs === 0 ? (
        <Card className="surface">
          <CardContent className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <ClipboardList className="h-14 w-14 mb-4 opacity-50" />
            <p className="font-medium text-foreground">No jobs booked for {humanDate}</p>
            <p className="text-sm">Import a PO or set an on-site date on a job to see it here.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary chips */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Card className="surface"><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center"><Briefcase className="h-5 w-5 text-primary" /></div>
              <div><p className="text-2xl font-bold font-heading">{report.total_jobs}</p><p className="text-xs text-muted-foreground">Jobs</p></div>
            </CardContent></Card>
            <Card className="surface"><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-cyan-400/15 flex items-center justify-center"><HardHat className="h-5 w-5 text-cyan-400" /></div>
              <div><p className="text-2xl font-bold font-heading">{report.total_trades}</p><p className="text-xs text-muted-foreground">Trade types</p></div>
            </CardContent></Card>
            <Card className="surface col-span-2 sm:col-span-1"><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-400/15 flex items-center justify-center"><CalendarClock className="h-5 w-5 text-amber-400" /></div>
              <div><p className="text-sm font-bold font-heading">{humanDate}</p><p className="text-xs text-muted-foreground">On site</p></div>
            </CardContent></Card>
          </div>

          {/* AI briefing */}
          {report.ai_briefing && (
            <Card className="surface overflow-hidden">
              <div className="brand-gradient h-1" />
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg brand-gradient flex items-center justify-center shadow-glow"><Sparkles className="h-4 w-4 text-white" /></div>
                  <h3 className="font-semibold">AI Morning Briefing</h3>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-line text-foreground/90">{report.ai_briefing}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Trades needed */}
            <Card className="surface">
              <CardContent className="p-5">
                <h3 className="font-semibold flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-primary" /> Trades needed</h3>
                <div className="space-y-2">
                  {report.trades_summary.map((t) => (
                    <div key={t.trade} className="flex items-center justify-between p-2.5 rounded-xl bg-secondary/50" data-testid={`trade-${t.trade}`}>
                      <span className="text-sm font-medium">{t.trade}</span>
                      <Badge className="brand-gradient text-white rounded-lg">{t.count}</Badge>
                    </div>
                  ))}
                  {report.trades_summary.length === 0 && <p className="text-sm text-muted-foreground">No trades specified on these jobs.</p>}
                </div>
              </CardContent>
            </Card>

            {/* Jobs list */}
            <Card className="surface lg:col-span-2">
              <CardContent className="p-5">
                <h3 className="font-semibold flex items-center gap-2 mb-4"><FileBarChart2 className="h-4 w-4 text-primary" /> Jobs on site</h3>
                <div className="space-y-3">
                  {report.jobs.map((j) => (
                    <div key={j.id} className="rounded-xl border border-border p-4" data-testid={`report-job-${j.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold">{j.title}</h4>
                        <Badge className={`${j.status ? `status-${j.status}` : ''} rounded-lg text-xs capitalize`}>{(j.status || 'pending').replace('_', ' ')}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                        {j.supplier_name && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{j.supplier_name}</span>}
                        {j.po_reference && <span className="flex items-center gap-1 font-mono"><Hash className="h-3.5 w-3.5" />{j.po_reference}</span>}
                        {j.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.address}</span>}
                      </div>
                      {(j.trades_required || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {j.trades_required.map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default NextDayReportPage;
